// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { ArticleContainer } from '../article-container';
import { ConnectionAreaUsageVO } from '@shared/vo/meta-element.vo';
import { AdvancedLine } from '../../model/advanced-line';
import { Connection } from '../connection';
import { Matrix4, Vector3 } from 'three';
import { ArticleContainerUtils } from './article-container-utils';
import { OverlapInfo } from '../../model/overlap-info';
import { ContainerService } from '../container.service';
import { Rectangle3D } from '../../model/rectangle-3d';

@Injectable({ providedIn: 'root' })
export class LineComponentUtils {
  constructor(private _componentUtils: ContainerService,
              private _articleLineHolder: ArticleLineHolder
  ) {}

  public getArticleLine(articleContainer: ArticleContainer, acceptingArea: ConnectionAreaUsageVO, baseLine: AdvancedLine): AdvancedLine {
    return this._articleLineHolder.getArticleLineByAcceptingUsage(articleContainer, acceptingArea, baseLine);
  }

  /**
   * Split rangeOnLine connection area in lines, which define free spaces
   * @param acceptingArea accepting area
   * @param acceptingConnection accepting area connection object
   * @param acceptingTransform
   * @param excludingArticleIds
   * @param mergeLines
   * @param inverseTransform
   * @return array of lines
   */
  public splitRangeOnLineConnectionInLines(
    acceptingArea: ConnectionAreaUsageVO,
    acceptingConnection: Connection,
    acceptingTransform: Matrix4 = null,
    excludingArticleIds: string[] = null,
    mergeLines: boolean = true,
    inverseTransform: boolean = false
  ): AdvancedLine[] {
    const baseLine: AdvancedLine = this._componentUtils.calculateBaseLine(acceptingConnection, acceptingTransform, inverseTransform);
    if (!acceptingArea) {
      return [baseLine];
    }

    const containers: ArticleContainer[] = this._componentUtils.getConnectedArticleContainersByAcceptingAreaId(acceptingArea.identity);
    ArticleContainerUtils.sortArticleContainersByDistanceOfPosition(containers, baseLine.start);
    let freeSpace: AdvancedLine[] = [baseLine];
    for (const articleContainer of containers) {
      if (!excludingArticleIds || excludingArticleIds.indexOf(articleContainer.articleGUID) < 0) {
        const line: AdvancedLine = this.getArticleLine(articleContainer, acceptingArea, baseLine);
        if (line) {
          freeSpace = this._componentUtils.splitLines(freeSpace, line, mergeLines);
        }
      }
    }

    const freeOverlappedSpace: AdvancedLine[] = this.splitRangeOnLineConnectionInOverlappedLines(
      acceptingArea,
      baseLine,
      containers,
      excludingArticleIds
    );

    return freeSpace.concat(freeOverlappedSpace);
  }

  public getFreeOverlappedLine(
    acceptingArea: ConnectionAreaUsageVO,
    baseLine: AdvancedLine,
    leftContainer: ArticleContainer,
    rightContainer: ArticleContainer
  ): AdvancedLine {
    const leftLine: AdvancedLine = this.getArticleLine(leftContainer, acceptingArea, baseLine);
    const rightLine: AdvancedLine = this.getArticleLine(rightContainer, acceptingArea, baseLine);
    if (leftLine && rightLine && AdvancedLine.isOverlapEnabled(leftLine, rightLine) && leftLine.hasPoint(rightLine.start)) {
      const overlapLeft: number = leftLine.overlapRight - leftLine.end.distanceTo(rightLine.start);
      const overlapRight: number = rightLine.overlapLeft - rightLine.start.distanceTo(leftLine.end);
      if (overlapLeft >= 0 && overlapRight >= 0) {
        return new AdvancedLine(
          rightLine.start,
          leftLine.end,
          new OverlapInfo(leftLine.overlapTypes, overlapLeft, overlapRight, leftLine.overlapRightTypes, rightLine.overlapLeftTypes)
        );
      }
    }
    return null;
  }

  public splitRangeOnLineConnectionInOverlappedLines(
    acceptingArea: ConnectionAreaUsageVO,
    baseLine: AdvancedLine,
    containers: ArticleContainer[],
    excludingArticleIds: string[] = null
  ): AdvancedLine[] {
    const overlappedLines: AdvancedLine[] = [];
    for (let i: number = 0; i < containers.length; i++) {
      const leftContainer: ArticleContainer = containers[i];
      if (!excludingArticleIds || !excludingArticleIds.includes(leftContainer.articleGUID)) {
        let rightContainer: ArticleContainer = null;
        for (let j: number = i + 1; j < containers.length; j++) {
          if (!excludingArticleIds || !excludingArticleIds.includes(containers[j].articleGUID)) {
            rightContainer = containers[j];
            break;
          }
        }

        if (leftContainer && rightContainer) {
          const line: AdvancedLine = this.getFreeOverlappedLine(acceptingArea, baseLine, leftContainer, rightContainer);
          if (line) {
            overlappedLines.push(line);
          }
        }
      }
    }

    return overlappedLines;
  }

  /**
   * Split rangeOnLine connection area in rectangles, which define free spaces
   * @param acceptingArea accepting area
   * @param acceptingConnection accepting area connection object
   * @param excludingArticleIds articleUsage GUIDs to exclude from splitting
   * @return array of rectangles
   */
  public splitRangeOnLineConnection(
    acceptingArea: ConnectionAreaUsageVO,
    acceptingConnection: Connection,
    acceptingTransform: Matrix4 = null,
    excludingArticleIds: string[] = null,
    inverseTransform: boolean = false
  ): Rectangle3D[] {
    const freeSpace: AdvancedLine[] = this.splitRangeOnLineConnectionInLines(
      acceptingArea,
      acceptingConnection,
      acceptingTransform,
      excludingArticleIds,
      true,
      inverseTransform
    );
    return this.convertFreeSpaceLinesToRectangles(freeSpace, acceptingConnection.localRectangle, acceptingTransform, inverseTransform);
  }

  private convertFreeSpaceLinesToRectangles(
    lines: AdvancedLine[],
    baseRectangle: Rectangle3D,
    transformMatrix: Matrix4 = null,
    inverseTransform: boolean = false
  ): Rectangle3D[] {
    const baseRectangleToConvert: Rectangle3D = Rectangle3D.fromArray(baseRectangle.toArray());
    if (transformMatrix) {
      baseRectangleToConvert.transform(transformMatrix);
    }
    const result: Rectangle3D[] = [];
    for (const line of lines) {
      result.push(this._componentUtils.convertLineToRectangle(line, baseRectangleToConvert, inverseTransform));
    }
    return result;
  }

  /**
   * Calculate rectangle for connection cube. Method applies article container rotation and translation to rectangle points.
   * @param connectionCube connection cube
   * @return rectangle
   *
   */
  public getConnectionRectangleByConnector(
    connectionCube: Connection,
    excludingArticleIds: string[] = null,
    inGlobal: boolean = false,
    inverseTransform: boolean = false
  ): Rectangle3D {
    const articleContainer: ArticleContainer = this._componentUtils.getArticleContainer(connectionCube);
    const translationMatrix: Matrix4 = this._componentUtils.getArticleTransformationMatrix(articleContainer);
    const ranges: Rectangle3D[] = this.splitRangeOnLineConnection(
      connectionCube.metaElementUsage as ConnectionAreaUsageVO,
      connectionCube,
      inGlobal ? translationMatrix : null,
      excludingArticleIds,
      inverseTransform
    );
    let connectorPosition: Vector3 = connectionCube.mesh.position.clone();
    if (inGlobal) {
      connectorPosition = connectorPosition.applyMatrix4(translationMatrix);
    }
    let minDistance: number = Number.MAX_VALUE;
    let closestRect: Rectangle3D = null;
    let distance: number;
    for (const rect of ranges) {
      distance = this._componentUtils.findMinDistanceToRectanglePoints(rect, connectorPosition);
      if (!closestRect || minDistance > distance) {
        closestRect = rect;
        minDistance = distance;
      }
    }
    if (!inGlobal) {
      // in case when inGlobal set to true ranges and closesRect are already calculated in global space
      closestRect.transform(translationMatrix);
    }
    return closestRect;
  }
}
